import type { InspectionTemplate } from 'inspection-types';

import { applyChange } from '../model/changes';
import { createOpKey, type OpKey, type PendingOp } from '../model/operations';
import { sortSections } from '../model/templateProjection';
import type { CustomizationAction } from './actions';
import {
  createInitialState,
  type CustomizationState,
} from './customizationState';

function resolveExpansion(
  template: InspectionTemplate,
  current: Record<string, boolean>
): Record<string, boolean> {
  return Object.fromEntries(
    template.sections.map(section => [
      section.id,
      current[section.id] ?? !section.config?.collapsedByDefault,
    ])
  );
}

function resolveActiveSection(
  template: InspectionTemplate,
  current: CustomizationState['activeSectionId']
): CustomizationState['activeSectionId'] {
  const stillExists = template.sections.some(section => section.id === current);

  if (stillExists) {
    return current;
  }

  const [firstSection] = sortSections(template.sections);

  return firstSection?.id ?? null;
}

function withoutKey<V>(
  record: Readonly<Record<OpKey, V>>,
  key: OpKey
): Record<OpKey, V> {
  const entries = Object.entries(record).filter(
    ([entryKey]) => entryKey !== key
  );

  return Object.fromEntries(entries);
}

type ActionOf<T extends CustomizationAction['type']> = Extract<
  CustomizationAction,
  { type: T }
>;

function startOperation(
  state: CustomizationState,
  { op }: ActionOf<'OP_STARTED'>
): CustomizationState {
  const opKey = createOpKey(op.kind, op.targetId);

  return {
    ...state,
    pendingOps: { ...state.pendingOps, [opKey]: op },
    failures: withoutKey(state.failures, opKey),
  };
}

// The confirm and fail handlers ignore an op that is no longer pending: a template
// switch resets pendingOps while its request is still in flight
function confirmOperation(
  state: CustomizationState,
  { opKey, newVersion }: ActionOf<'OP_SUCCEEDED'>
): CustomizationState {
  const op: PendingOp | undefined = state.pendingOps[opKey];

  if (!op || !state.replica) {
    return state;
  }

  const changed = applyChange(state.replica, op.change);

  return {
    ...state,
    replica: { ...changed, version: newVersion },
    pendingOps: withoutKey(state.pendingOps, opKey),
  };
}

function failOperation(
  state: CustomizationState,
  { opKey, failure }: ActionOf<'OP_FAILED'>
): CustomizationState {
  const op: PendingOp | undefined = state.pendingOps[opKey];

  if (!op) {
    return state;
  }

  const pendingOps = withoutKey(state.pendingOps, opKey);

  if (failure.outcome === 'unknown') {
    return { ...state, pendingOps, staleReason: 'outcomeUnknown' };
  }

  if (op.kind === 'reorder') {
    return { ...state, pendingOps };
  }

  return {
    ...state,
    pendingOps,
    failures: { ...state.failures, [opKey]: { op, failure } },
  };
}

function abortOperation(
  state: CustomizationState,
  { opKey }: ActionOf<'OP_ABORTED'>
): CustomizationState {
  return { ...state, pendingOps: withoutKey(state.pendingOps, opKey) };
}

function loadReplica(
  state: CustomizationState,
  { template }: ActionOf<'REPLICA_LOADED'>
): CustomizationState {
  return {
    ...state,
    replica: template,
    replicaStatus: 'ready',
    staleReason: null,
    refreshAttempt: { status: 'idle' },
    pendingOps: {},
    failures: {},
    expandedBySectionId: resolveExpansion(template, state.expandedBySectionId),
    activeSectionId: resolveActiveSection(template, state.activeSectionId),
  };
}

export function customizationReducer(
  state: CustomizationState,
  action: CustomizationAction
): CustomizationState {
  switch (action.type) {
    case 'IDENTITY_CHANGED':
      return createInitialState(action.identity);

    case 'REPLICA_LOAD_STARTED':
      return { ...state, replicaStatus: 'loading' };

    case 'REPLICA_LOADED':
      return loadReplica(state, action);

    case 'REPLICA_LOAD_FAILED':
      return { ...state, replicaStatus: action.failure };

    case 'OP_STARTED':
      return startOperation(state, action);

    case 'OP_SUCCEEDED':
      return confirmOperation(state, action);

    case 'OP_FAILED':
      return failOperation(state, action);

    case 'OP_ABORTED':
      return abortOperation(state, action);

    case 'REFRESH_REQUESTED':
      return { ...state, refreshAttempt: { status: 'inFlight' } };

    case 'REFRESH_FAILED':
      return {
        ...state,
        refreshAttempt: { status: 'failed', failure: action.failure },
      };

    case 'SECTION_TOGGLED':
      return {
        ...state,
        expandedBySectionId: {
          ...state.expandedBySectionId,
          [action.sectionId]: !state.expandedBySectionId[action.sectionId],
        },
      };

    case 'ACTIVE_SECTION_SET':
      return state.activeSectionId === action.sectionId
        ? state
        : { ...state, activeSectionId: action.sectionId };
  }
}
